import { Wallet, hdkey } from '@ethereumjs/wallet';
import { generateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import type { Hash, Hex } from 'viem';
import {
  createClient,
  encodePacked,
  hexToBytes,
  keccak256,
  webSocket,
} from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import {
  estimateGas,
  getBalance,
  getGasPrice,
  getTransactionCount,
  sendTransaction,
  signMessage,
} from 'viem/actions';

import type { Deal, SettleDealSignatureType } from '@sorare/wallet-shared';

import { EVM_ENDPOINT } from '#src/config.ts';
import { computeBridgeFact as computeSolanaBridgeFact } from '#src/lib/solana/index.ts';

import {
  approveMigratorMessage,
  computeFactData,
  depositETHtoBaseBankTx,
  ethereumBankTransferMessage,
  migrateCardsMessage,
  migrateEthMessage,
  settleDealMessage,
} from './contracts.ts';

const PATH = "m/44'/60'/0'/0/0";

interface TransferParams {
  senderAddress: string;
  receiverAddress: string;
  amount: bigint;
  feeAmount: bigint;
  deadline: string;
  salt: string;
  proxyAddress: string;
  data: string;
  contractAddress: string;
}

interface ConditionalTransferParams extends Omit<TransferParams, 'data'> {
  conditionalAmount: bigint;
  conditionalCurrency: string;
  conditionalNetwork: string;
  conditionalReceiverAddress: string;
  conditionalSalt: string;
}

function buildWalletClient() {
  return createClient({ transport: webSocket(EVM_ENDPOINT) });
}

class EthereumWallet {
  public static create() {
    const mnemonic = generateMnemonic(wordlist);

    const wallet = hdkey.EthereumHDKey.fromMnemonic(mnemonic)
      .derivePath(PATH)
      .getWallet();
    return new EthereumWallet(wallet);
  }

  public static load(privateKey: Hex) {
    const wallet = new Wallet(hexToBytes(privateKey));

    return new EthereumWallet(wallet);
  }

  private wallet: Wallet;

  public constructor(wallet: Wallet) {
    this.wallet = wallet;
  }

  public get address() {
    return this.wallet.getAddressString() as EthereumAddress;
  }

  public get privateKey() {
    return this.wallet.getPrivateKeyString();
  }

  private loadedWalletClient?: ReturnType<typeof buildWalletClient>;

  private get walletClient() {
    if (!this.loadedWalletClient) {
      this.loadedWalletClient = buildWalletClient();
    }

    return this.loadedWalletClient;
  }

  public depositTransaction(sorareAddress: string) {
    return depositETHtoBaseBankTx(sorareAddress);
  }

  public async signSettleDeal(deal: Deal, action: SettleDealSignatureType) {
    return this.sign(settleDealMessage(deal, action));
  }

  public async signApproveMigrator(nonce: string | number) {
    const { message, ...rest } = approveMigratorMessage(nonce);
    const signature = await this.sign(message);
    return { signature, ...rest };
  }

  public async signMigrateCards(
    cardIds: string[],
    expirationBlock: string | number
  ) {
    const message = migrateCardsMessage(cardIds, expirationBlock);
    return this.sign(message);
  }

  public async signMigrateEth(
    dealId: string | number,
    sendAmountInWei: bigint
  ) {
    return this.sign(migrateEthMessage(dealId, this.address, sendAmountInWei));
  }

  public async signEthereumBankTransfer(request: TransferParams) {
    return this.sign(ethereumBankTransferMessage(request));
  }

  public async signEthereumConditionalBankTransfer(
    request: ConditionalTransferParams
  ) {
    const fact = computeSolanaBridgeFact({
      conditionalAmount: request.conditionalAmount,
      conditionalSalt: request.conditionalSalt,
      conditionalReceiverAddress: request.conditionalReceiverAddress,
      conditionalBridgeCallerAddress: request.proxyAddress,
      conditionalNetwork: request.conditionalNetwork,
      conditionalCurrency: request.conditionalCurrency,
    });

    return this.signEthereumBankTransfer({
      ...request,
      data: fact,
    });
  }

  public async signBaseBankWithdrawal(request: {
    senderAddress: string;
    receiverAddress: string;
    destinationAddress: string;
    amount: bigint;
    feeAmount: bigint;
    deadline: string;
    salt: string;
    proxyAddress: string;
    contractAddress: string;
  }) {
    const factData = computeFactData(
      request.destinationAddress,
      request.amount,
      request.salt
    );

    return this.signEthereumBankTransfer({
      ...request,
      data: factData,
    });
  }

  public async getBalance() {
    return getBalance(this.walletClient, {
      address: this.address,
    });
  }

  public async getGasPrice() {
    return getGasPrice(this.walletClient);
  }

  public async getConfirmedNonce() {
    return getTransactionCount(this.walletClient, {
      address: this.address,
      blockTag: 'latest',
    });
  }

  public async estimateGas({
    data,
    to,
    value,
  }: {
    data: Hex;
    to: Hex;
    value: string;
  }) {
    return estimateGas(this.walletClient, {
      to,
      data,
      value: BigInt(value),
      gasPrice: 0n,
    });
  }

  public async sendTransaction({
    to,
    data,
    value,
    gasPrice,
    gas,
    nonce,
  }: {
    to: Hex;
    data: Hex;
    value: string;
    gasPrice: bigint;
    gas: bigint;
    nonce?: number;
  }): Promise<Hash> {
    return sendTransaction(this.walletClient, {
      account: privateKeyToAccount(this.privateKey),
      chain: undefined,
      data,
      to,
      value: BigInt(value),
      gas,
      gasPrice,
      nonce,
    });
  }

  private async sign(message: Hex) {
    return signMessage(this.walletClient, {
      account: privateKeyToAccount(this.privateKey),
      message: { raw: encodePacked(['bytes'], [keccak256(message)]) },
    });
  }
}

export default EthereumWallet;
